import api from "@/lib/api";
import { toast } from "sonner";

/**
 * Open a certificate PDF in a new tab using a blob URL so the JWT stays
 * on the Authorization header (instead of being baked into the URL via
 * a `?auth=` query string, which leaks into browser history & referrers).
 *
 * Returns a promise that resolves once the new tab has been opened (or
 * an error toast has been shown). Pass `download = true` to trigger a
 * file download instead of an in-browser preview.
 */
export async function openCertificatePdf(certId, { download = false, filename } = {}) {
    if (!certId) return;
    const t = toast.loading(download ? "Preparing download…" : "Preparing your certificate…");
    try {
        const { data } = await api.get(`/certificates/${certId}/download`, {
            responseType: "blob",
        });
        const blobUrl = URL.createObjectURL(
            new Blob([data], { type: "application/pdf" }),
        );
        if (download) {
            const a = document.createElement("a");
            a.href = blobUrl;
            a.download = filename || `certificate-${certId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
        } else {
            window.open(blobUrl, "_blank", "noopener,noreferrer");
        }
        // Revoke after a delay so the new tab has time to load it.
        setTimeout(() => URL.revokeObjectURL(blobUrl), 60_000);
        toast.success(download ? "Downloaded" : "Certificate ready", { id: t });
    } catch (e) {
        toast.error(
            e?.response?.status === 401
                ? "Your session expired — please sign in again."
                : "Could not open certificate. Try again.",
            { id: t },
        );
    }
}
